/**
 * Loads every device row for an organization by calling the GraphQL query
 * `GetDevicesByOrganization` repeatedly until:
 *  - the API returns no `nextToken` (we got everything) or
 *  - we hit the caller's `maxPages` safety cap
 
 */

import type { GetDevicesByOrganizationQuery } from 'gqlHooks';
import type { ApolloError } from '@apollo/client';
import { CONSTANTS } from 'utils/constants';

// shape of a single device row returned by `GetDevicesByOrganization`
type Device = GetDevicesByOrganizationQuery['devices']['items'][number];

// minimal type for fetch one page of devices from the organization
export type FetchOrganizationDevicesPage = (options: {
  variables: {
    organizationId: string;
    limit: number;
    nextToken: string | null;
    // server-side prefix search for IMEI/device name.
    // pass undefined when search bar is empty = "give me the full device list in the organization"
    searchTerm?: string;
  };
}) => Promise<{
  data?: GetDevicesByOrganizationQuery;
  error?: ApolloError;
}>;

export type FetchAllOrganizationDevicesParams = {
  organizationId: string; // required
  searchTerm?: string; // optional search filter - empty string should be converted to undefined
  pageSize?: number; // default: 500
  maxPages?: number; // if we stop because we hit this cap but `nextToken` is still available, `truncated` = true
  fetchPage: FetchOrganizationDevicesPage; // function to fetch one page of devices
};

export type FetchAllOrganizationDevicesResult = {
  devices: Device[]; // all device rows accumulated across pages
  truncated: boolean; // true if we hit `maxPages` but `nextToken` is still available; UI should show a warning
  error: ApolloError | null;
};

export async function fetchAllOrganizationDevices({
  organizationId,
  searchTerm,
  pageSize = CONSTANTS.DEVICE_PAGE_SIZE,
  maxPages = CONSTANTS.DEVICE_PAGE_MAX_PAGES,
  fetchPage,
}: FetchAllOrganizationDevicesParams): Promise<FetchAllOrganizationDevicesResult> {
  // early exit - nothing to fetch
  if (!organizationId) {
    return { devices: [], truncated: false, error: null };
  }

  // initialize accumulator and pagination state
  const accumulated: Device[] = [];
  let nextToken: string | null | undefined = null;
  let pagesFetched = 0;

  try {
    do {
      const { data, error: pageError } = await fetchPage({
        variables: {
          organizationId,
          limit: pageSize,
          nextToken: nextToken ?? null,
          searchTerm: searchTerm || undefined, // undefined = full list
        },
      });
      if (pageError) throw pageError;
      const page = data?.devices;
      if (page?.items?.length) accumulated.push(...page.items);
      nextToken = page?.nextToken ?? null;
      pagesFetched += 1;
    } while (nextToken && pagesFetched < maxPages);

    const truncated = Boolean(nextToken && pagesFetched >= maxPages);

    if (truncated) {
      // eslint-disable-next-line no-console
      console.warn('fetchAllOrganizationDevices: hit maxPages guard', {
        organizationId,
        pagesFetched,
        loaded: accumulated.length,
        maxPages,
      });
    }

    return { devices: accumulated, truncated, error: null };
  } catch (e) {
    // export all or nothing
    return { devices: [], truncated: false, error: e as ApolloError };
  }
}
